// Navigate in Google Maps for the office (BL-018, the maps design v3, place 8): one plain Maps URL for every device,
// with the same words everywhere. Google's Maps URLs need no key, so nothing is billed to us; the
// Google Maps app opens it where it's installed, the browser otherwise. Flowology never steers.
//
// This is the one link that carries a customer's address out of Flowology, which is the point of it:
// nothing else does. The destination is the address as written, unless the point is our own and
// exact (a pin the office placed, or where the iPad arrived and stayed); then it's the point.
// The same builder as packages/domain's navigate.ts (the iPad's), kept here because ui-web may import
// only tokens and kernel; apps/web's navigate test checks the two agree.

/** The words on the link, on the iPad, a phone and the office alike. */
export const NAVIGATE_LABEL = 'Navigate in Google Maps';
/** Said beside it where the address leaving Flowology should be said (the iPad's drive card). */
export const NAVIGATE_NOTE = 'Navigate opens Google Maps, which gets this address.';

/** A point, with where it came from; only our own exact ones are used as the destination. */
export interface NavigatePoint {
  lat: number;
  lng: number;
  source: string;
}

const EXACT_SOURCES = new Set(['office', 'arrival']);
/** Google's limit on a Maps URL. */
const MAX_URL = 2048;
const BASE = 'https://www.google.com/maps/dir/?api=1';

/** The Maps URL that opens the drive to an address (or our own point), or null with neither. */
export function navigateUrl(to: { address?: string | null; point?: NavigatePoint | null }): string | null {
  const p = to.point;
  const exact =
    p && EXACT_SOURCES.has(p.source) && Number.isFinite(p.lat) && Number.isFinite(p.lng)
      ? `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`
      : null;
  const address = (to.address ?? '')
    .split(/\s*\n\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(', ')
    .replace(/\s+/g, ' ');
  const destination = exact ?? address;
  if (!destination) return null;
  const url = `${BASE}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
  return url.length <= MAX_URL ? url : null;
}
